const registeredLocales = new Map();
const registeredDomains = new Map();

/**
 * @typedef {Object} LocaleData
 * @property {string} code - Locale code
 * @property {string} name - Locale name
 * @property {string} lang - Language
 * @property {string} langName - Language name
 * @property {string} region - Region
 * @property {string} regionName - Region name
 * @property {string} currency - Currency code
 * @property {string} tld - TLD
 */

/**
 * @typedef {Object} LangData
 * @property {string} lang - Language
 * @property {string} langName - Language name
 * @property {Object.<string, RegionData>} regions - Region
 */

/**
 * @typedef {Object} RegionData
 * @property {string} regionName - Region name
 * @property {string} tld - TLD
 */

/**
 * Register lang & regions
 * @param {LangData} langData
 * @returns {void}
 */
function registerLang(langData) {
    if (!langData.lang || typeof langData.lang !== 'string') {
        throw new TypeError('Missing localeData.lang');
    }

    if (!langData.regions) {
        throw new TypeError('Missing localeData.regions');
    }

    const lang = langData.lang.toLowerCase();
    const langName = langData.langName;
    Object.entries(langData.regions).forEach(([regionCode, {regionName, currency, tld, order}]) => {
        const region = regionCode.toLowerCase();
        const name = `${regionName} (${langName})`;
        const code = getCode(region, lang);
        const key = code.toLowerCase();

        if (registeredLocales.has(key)) {
            throw new Error(`Locale already registered: '${code}'`);
        }
        const locale = {code, name, lang, langName, region, regionName, currency, tld, order};

        // register locales
        registeredLocales.set(key, locale);

        // register domains
        if (region === 'int' || lang === tld) {
            if (registeredDomains.has(tld)) {
                throw new Error(`Detected several default locales for domain: '${tld}'`);
            }

            registeredDomains.set(tld, code);
        }
    });
}

function getCode(region, lang) {
    return region === 'int' ? lang : `${lang}-${region.toUpperCase()}`;
}

/**
 * Array of registered locale codes
 * @returns {string[]} Array of registered locale codes
 */
function getLocaleCodes() {
    return Array.from(registeredLocales.values())
        .sort((a, b) => a.order - b.order)
        .map(({code}) => code);
}

/**
 * Array of registered locales
 * @returns {LocaleData[]} Array of registered locales
 */
function getLocales() {
    return Array.from(registeredLocales.values()).sort((a, b) => a.order - b.order);
}

/**
 * Array of registered domains
 * @returns {string[]} Array of registered domains
 */
function getDomains() {
    return Array.from(registeredDomains.keys());
}

/**
 * Get locale by code
 * @param {string} code Locale code
 * @returns {LocaleData | undefined} Locale data or undefined
 */
function getLocale(code) {
    if (!code || typeof code !== 'string') {
        return undefined;
    }

    return registeredLocales.get(code.toLowerCase());
}

/**
 * Checks if locale registered
 * @param {string} code Locale code
 * @returns {boolean} Boolean
 */
function isRegistered(code) {
    if (!code || typeof code !== 'string') {
        return false;
    }

    return registeredLocales.has(code.toLowerCase());
}

/**
 * Get locale code by domain
 * @param {string} tld TLD
 * @returns {string | undefined} Locale data or undefined
 */
function getLocaleCodeByTld(tld) {
    if (!tld || typeof tld !== 'string') {
        return undefined;
    }

    return registeredDomains.get(tld.toLowerCase());
}

module.exports = {
    registerLang,
    getLocaleCodes,
    getLocales,
    getDomains,
    getLocale,
    isRegistered,
    getLocaleCodeByTld,
    getCode,
};
